import React, { useState, useEffect, useMemo } from 'react'
import { updateUser } from "@api/usersController";
import { useBranches } from "@api";
import { useUser } from "@auth/UserContext";
import { Modal, Button } from "@components";
import { toast } from "@components/toast/Toast";
import TeamMemberForm from '../forms/TeamMemberForm';
import { useForm } from 'react-hook-form';

const EditTeamMember = ({editTeamMemberDetails, onClose, onComplete, roles}) => {

  const { user, reload: reloadUser } = useUser();
  const [loading, setLoading] = useState(false);
  const { data: branchesData = [] } = useBranches();

  const { control, handleSubmit, setValue, reset, watch, formState: { errors, isValid }, clearErrors, setError } = useForm({
    defaultValues: {
      name: editTeamMemberDetails.name ?? "",
      email: editTeamMemberDetails.email ?? "",
      roleId: editTeamMemberDetails.roleId ?? "",
      branchId: editTeamMemberDetails.branchId ?? "",
    }
  });

  // Memoize branches for the form
  const branches = useMemo(() => {
    const mappedBranches = branchesData.map(x => ({
      value: x.id ?? "",
      display: x.name,
    }));
    
    return mappedBranches;
  }, [branchesData]);

  const onSubmit = async (values) => {
    setLoading(true);
    await saveTeamMemberEdits(values);
    // If the edited member is the current user, refresh their memberships so a role change
    // takes effect immediately (e.g. AI features / spacebar) without a page refresh.
    if (String(editTeamMemberDetails.id) === String(user?.id)) {
      await reloadUser();
    }
    setLoading(false);

    toast({ description: `Updated "${values.name}" team member` });
    onComplete();
    handleClose();
  };

  const saveTeamMemberEdits = async (values) => {
    await updateUser(
      editTeamMemberDetails.id,
      values.name,
      values.branchId || null,
      values.roleId
    );
  }

  const handleClose = () => {
    reset();
    clearErrors();
    onClose();
  }

  return (
    <Modal isOpen={!!editTeamMemberDetails} onClose={handleClose} title="Edit team member" size="sm">
      <div className="p-5">
        <TeamMemberForm 
          editing
          handleSubmit={handleSubmit}
          onSubmit={onSubmit}
          control={control}
          errors={errors}
          branches={branches}
          roles={roles}
          hasBranches={!!branches}
        />
      </div>
      <div className="p-4 flex gap-2 justify-end">
        <Button onClick={handleClose} disabled={loading} color="secondary">Cancel</Button>
        <Button 
          type="submit" 
          onClick={handleSubmit(onSubmit)} 
          color="primary" 
          disabled={!isValid}
          submitting={loading}
        >Save changes</Button>
      </div>
    </Modal>
  )
}

export default EditTeamMember